import { useEffect, useState } from "react";

export type Breakpoint = "mobile" | "tablet" | "desktop" | "widescreen";

export interface BreakpointInfo {
  breakpoint: Breakpoint;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isWidescreen: boolean;
  width: number;
  height: number;
}

export function useBreakpoint(): BreakpointInfo {
  const [breakpointInfo, setBreakpointInfo] = useState<BreakpointInfo>(() => {
    const width = typeof window !== "undefined" ? window.innerWidth : 1920;
    const height = typeof window !== "undefined" ? window.innerHeight : 1080;

    const getBreakpoint = (width: number): Breakpoint => {
      if (width < 640) return "mobile";
      if (width < 1024) return "tablet";
      if (width < 1440) return "desktop";
      return "widescreen";
    };

    const breakpoint = getBreakpoint(width);

    return {
      breakpoint,
      isMobile: breakpoint === "mobile",
      isTablet: breakpoint === "tablet",
      isDesktop: breakpoint === "desktop",
      isWidescreen: breakpoint === "widescreen",
      width,
      height,
    };
  });

  useEffect(() => {
    const checkSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      const getBreakpoint = (width: number): Breakpoint => {
        if (width < 640) return "mobile";
        if (width < 1024) return "tablet";
        if (width < 1440) return "desktop";
        return "widescreen";
      };

      const breakpoint = getBreakpoint(width);

      setBreakpointInfo((prev) => {
        if (prev.width === width && prev.height === height) return prev;

        return {
          breakpoint,
          isMobile: breakpoint === "mobile",
          isTablet: breakpoint === "tablet",
          isDesktop: breakpoint === "desktop",
          isWidescreen: breakpoint === "widescreen",
          width,
          height,
        };
      });
    };

    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  return breakpointInfo;
}

// Hook adicional para detectar orientação
export function useOrientation() {
  const [orientation, setOrientation] = useState<"portrait" | "landscape">(
    () => {
      if (typeof window !== "undefined") {
        return window.innerHeight > window.innerWidth
          ? "portrait"
          : "landscape";
      }
      return "landscape";
    },
  );

  useEffect(() => {
    const checkOrientation = () => {
      setOrientation(
        window.innerHeight > window.innerWidth ? "portrait" : "landscape",
      );
    };

    checkOrientation();
    window.addEventListener("resize", checkOrientation);
    return () => window.removeEventListener("resize", checkOrientation);
  }, []);

  return orientation;
}

// Hook para detectar se é dispositivo touch
export function useTouchDevice() {
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const checkTouchDevice = () => {
      setIsTouchDevice(
        "ontouchstart" in window ||
          navigator.maxTouchPoints > 0 ||
          // @ts-expect-error/ban-ts-comment
          navigator.msMaxTouchPoints > 0,
      );
    };

    checkTouchDevice();
  }, []);

  return isTouchDevice;
}
